import { getFirestore, doc, runTransaction } from 'firebase/firestore'
import { getAuth } from 'firebase/auth'
import { getFunctions, httpsCallable } from 'firebase/functions'
import Database from '../types/Database'
import { getDiveStatusAfterCancellation } from '../utils/refundCalculation'

/**
 * Cancels a guided dive booking with a full credit refund.
 * Calls the processCreditRefund Cloud Function which atomically:
 * - Sets joinRequest.status to "cancelled"
 * - Sets transaction.payoutStatus to "credited", refundType to "credit"
 * - Sets transaction.platformCommission and netOwedToHost to 0
 * - Increments user.creditBalance by transaction.amount
 * - Updates dive status via getDiveStatusAfterCancellation()
 */
export async function cancelWithCredit(
  joinRequestId: string
): Promise<Database.CancellationResult> {
  const auth = getAuth()
  const currentUser = auth.currentUser

  if (!currentUser) {
    return { success: false, error: 'NOT_AUTHENTICATED' }
  }

  try {
    const functions = getFunctions()
    const processCreditRefund = httpsCallable<
      { joinRequestId: string },
      { success: boolean; error?: string }
    >(functions, 'processCreditRefund')

    const result = await processCreditRefund({ joinRequestId })

    if (result.data.success) {
      return { success: true }
    }

    const errorMessage = result.data.error ?? ''
    if (errorMessage.includes('not authenticated')) {
      return { success: false, error: 'NOT_AUTHENTICATED' }
    }
    if (errorMessage.includes('not authorized')) {
      return { success: false, error: 'NOT_AUTHORIZED' }
    }
    if (errorMessage.includes('not active')) {
      return { success: false, error: 'DIVE_NOT_ACTIVE' }
    }
    return { success: false, error: 'INVALID_STATE' }
  } catch (error: any) {
    if (error?.code === 'functions/unauthenticated') {
      return { success: false, error: 'NOT_AUTHENTICATED' }
    }
    if (error?.code === 'functions/permission-denied') {
      return { success: false, error: 'NOT_AUTHORIZED' }
    }
    if (error?.code === 'functions/failed-precondition') {
      return { success: false, error: 'INVALID_STATE' }
    }
    return { success: false, error: 'NETWORK_ERROR' }
  }
}

/**
 * Cancels a guided dive booking with a monetary refund via Stripe.
 * Validates eligibility client-side, then calls the processMonetaryRefund
 * Cloud Function which handles the Stripe refund and Firestore updates.
 */
export async function cancelWithMonetaryRefund(
  joinRequestId: string
): Promise<Database.CancellationResult> {
  const auth = getAuth()
  const currentUser = auth.currentUser

  if (!currentUser) {
    return { success: false, error: 'NOT_AUTHENTICATED' }
  }

  const db = getFirestore()

  try {
    // Client-side eligibility validation before calling Cloud Function
    const joinRequestRef = doc(db, 'joinRequests', joinRequestId)
    const joinRequestSnap = await runTransaction(db, async transaction => {
      return await transaction.get(joinRequestRef)
    })

    if (!joinRequestSnap.exists()) {
      return { success: false, error: 'INVALID_STATE' }
    }

    const joinRequest = joinRequestSnap.data() as Omit<
      Database.JoinRequest,
      'id'
    >

    if (
      joinRequest.status !== 'approved' ||
      joinRequest.paymentStatus !== 'paid'
    ) {
      return { success: false, error: 'INVALID_STATE' }
    }

    if (currentUser.uid !== joinRequest.requesterId) {
      return { success: false, error: 'NOT_AUTHORIZED' }
    }

    // Call the Cloud Function
    const functions = getFunctions()
    const processMonetaryRefund = httpsCallable<
      Database.ProcessMonetaryRefundRequest,
      Database.ProcessMonetaryRefundResponse
    >(functions, 'processMonetaryRefund')

    const result = await processMonetaryRefund({ joinRequestId })

    if (result.data.success) {
      return { success: true }
    }

    // Map Cloud Function error to CancellationError
    const errorMessage = result.data.error ?? ''
    if (errorMessage.includes('not authenticated')) {
      return { success: false, error: 'NOT_AUTHENTICATED' }
    }
    if (errorMessage.includes('not authorized')) {
      return { success: false, error: 'NOT_AUTHORIZED' }
    }
    if (errorMessage.includes('not active')) {
      return { success: false, error: 'DIVE_NOT_ACTIVE' }
    }
    if (errorMessage.includes('stripe') || errorMessage.includes('refund')) {
      return { success: false, error: 'STRIPE_REFUND_FAILED' }
    }
    return { success: false, error: 'INVALID_STATE' }
  } catch (error: any) {
    if (error?.code === 'functions/unauthenticated') {
      return { success: false, error: 'NOT_AUTHENTICATED' }
    }
    if (error?.code === 'functions/permission-denied') {
      return { success: false, error: 'NOT_AUTHORIZED' }
    }
    if (error?.code === 'functions/failed-precondition') {
      return { success: false, error: 'INVALID_STATE' }
    }
    return { success: false, error: 'NETWORK_ERROR' }
  }
}

/**
 * Cancels a buddy dive booking (no payment, no refund).
 * Runs a single Firestore transaction to atomically:
 * - Set joinRequest.status to "cancelled"
 * - Update dive status via getDiveStatusAfterCancellation()
 */
export async function cancelBuddyBooking(
  joinRequestId: string
): Promise<Database.CancellationResult> {
  const auth = getAuth()
  const currentUser = auth.currentUser

  if (!currentUser) {
    return { success: false, error: 'NOT_AUTHENTICATED' }
  }

  const db = getFirestore()

  try {
    await runTransaction(db, async transaction => {
      // 1. Read joinRequest document
      const joinRequestRef = doc(db, 'joinRequests', joinRequestId)
      const joinRequestSnap = await transaction.get(joinRequestRef)

      if (!joinRequestSnap.exists()) {
        throw new CancellationServiceError('INVALID_STATE')
      }

      const joinRequest = joinRequestSnap.data() as Omit<
        Database.JoinRequest,
        'id'
      >

      // 2. Validate status and payment (buddy dives have null paymentStatus)
      if (
        joinRequest.status !== 'approved' ||
        joinRequest.paymentStatus !== null
      ) {
        throw new CancellationServiceError('INVALID_STATE')
      }

      // 3. Verify caller is the requester
      if (currentUser.uid !== joinRequest.requesterId) {
        throw new CancellationServiceError('NOT_AUTHORIZED')
      }

      // 4. Read dive document
      const diveRef = doc(db, 'dives', joinRequest.diveId)
      const diveSnap = await transaction.get(diveRef)

      if (!diveSnap.exists()) {
        throw new CancellationServiceError('INVALID_STATE')
      }

      const dive = diveSnap.data() as Omit<Database.Dive, 'id'>

      // 5. Validate dive is not completed or cancelled
      if (dive.status === 'completed' || dive.status === 'cancelled') {
        throw new CancellationServiceError('DIVE_NOT_ACTIVE')
      }

      // 6. Perform atomic updates within the transaction
      // Update joinRequest status
      transaction.update(joinRequestRef, { status: 'cancelled' })

      // Update dive status
      const newDiveStatus = getDiveStatusAfterCancellation(dive.status)
      transaction.update(diveRef, { status: newDiveStatus })
    })

    return { success: true }
  } catch (error) {
    if (error instanceof CancellationServiceError) {
      return { success: false, error: error.code }
    }
    return { success: false, error: 'NETWORK_ERROR' }
  }
}

// --- Internal error class ---

class CancellationServiceError extends Error {
  constructor(public code: Database.CancellationError) {
    super(code)
    this.name = 'CancellationServiceError'
  }
}
